import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Textarea,
  VStack,
  useToast,
  Switch,
  Text,
  HStack,
  Flex,
  Image,
} from "@chakra-ui/react";
import { FaDollarSign } from "react-icons/fa";
import { FiLink } from "react-icons/fi";
import { debounce } from "lodash";
import { secureFetch } from "../data/secureFetch";
import { BANNER_IMAGE_RECOMMENDATION } from "../utils/bannerImage";
import useBannerImageUpload from "../hooks/useBannerImageUpload";
const { formatDollarsToCents } = require("../store/calc-helpers");

const EditCampaign = () => {
  const { organizationId } = useSelector((state) => state.user);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: "",
    goal: "",
    startDate: "",
    endDate: "",
    url: "",
    description: "",
    status: "active",
  });
  const [showGoal, setShowGoal] = useState(false);
  const [showDates, setShowDates] = useState(false);
  const [cleanedUrl, setCleanedUrl] = useState("");
  const [urlAvailable, setUrlAvailable] = useState(true);
  const [manualUrlEdit, setManualUrlEdit] = useState(false);
  const [currentBannerUrl, setCurrentBannerUrl] = useState("");
  const [removeBannerImage, setRemoveBannerImage] = useState(false);
  const {
    bannerImageDataUrl,
    bannerImagePreview,
    bannerImageFileName,
    bannerImageWarning,
    handleBannerImageChange: handleBannerFileChange,
    resetBannerImage,
  } = useBannerImageUpload();

  // Wrap the hook handler to also clear the "remove" flag when a new file is chosen.
  const handleBannerImageChange = async (e) => {
    await handleBannerFileChange(e);
    if (e.target.files?.[0]) {
      setRemoveBannerImage(false);
    }
  };

  const checkUrlAvailability = useMemo(() => {
    const debounced = debounce(async (url) => {
      if (!url) {
        setUrlAvailable(false);
        return;
      }
      const payload = { url };
      // Pass the current campaign id when editing so the same URL is considered available.
      if (id) {
        payload.campaignId = id;
      }
      const response = await secureFetch(`/api/campaigns/check-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: payload,
      });
      const { available } = response;
      setUrlAvailable(available);
    }, 500);
    return debounced;
  }, [id]);

  useEffect(
    () => () => {
      checkUrlAvailability.cancel();
    },
    [checkUrlAvailability],
  );

  // Auto-generate URL based on campaign name if user hasn't manually edited it.
  useEffect(() => {
    if (!manualUrlEdit || formData.url === "") {
      const formattedUrl = formData.name
        .trim()
        .replace(/^[^a-zA-Z0-9]+/, "") // Remove leading non-alphanumeric characters
        .replace(/[^a-zA-Z0-9]+/g, "_") // Replace other non-alphanumeric characters with underscores
        .replace(/_+$/, "") // Remove trailing underscores
        .toLowerCase();
      if (formattedUrl !== formData.url) {
        setFormData((prev) => ({ ...prev, url: formattedUrl }));
      }
      setCleanedUrl(formattedUrl);
      checkUrlAvailability(formattedUrl);
    }
  }, [formData.name, formData.url, manualUrlEdit, checkUrlAvailability]);

  // Fetch campaign details and autopopulate form fields
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const response = await secureFetch(`/api/campaigns/id/${id}`);
        if (response.ok) {
          const data = response;
          setFormData({
            name: data.name,
            goal: data.goal ? (data.goal / 100).toString() : "",
            startDate: data.start_date ? data.start_date.split("T")[0] : "",
            endDate: data.end_date ? data.end_date.split("T")[0] : "",
            url: data.url,
            description: data.description || "",
            status: data.status || "active",
          });
          if (data.goal) setShowGoal(true);
          if (data.start_date || data.end_date) setShowDates(true);
          setCleanedUrl(data.url);
          checkUrlAvailability(data.url);
          setCurrentBannerUrl(data.banner_image_url || "");
          resetBannerImage();
          setRemoveBannerImage(false);
        } else {
          throw new Error("Failed to fetch campaign");
        }
      } catch (error) {
        toast({
          title: "Error",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchCampaign();
  }, [id, toast, checkUrlAvailability, resetBannerImage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "goal" && !/^\d*\.?\d{0,2}$/.test(value)) {
      return;
    }
    if (name === "url") {
      setManualUrlEdit(true);
      // When editing the URL field, reformat based on the current value
      const formattedUrl = value
        .trim()
        .replace(/^[^a-zA-Z0-9]+/, "")
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/_+$/, "")
        .toLowerCase();
      setCleanedUrl(formattedUrl);
      checkUrlAvailability(formattedUrl);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const generateRandomUrl = () => {
    const randomId = Math.random().toString(36).substr(2, 10);
    setFormData((prev) => ({ ...prev, url: randomId }));
    setCleanedUrl(randomId);
    setManualUrlEdit(true);
    checkUrlAvailability(randomId);
  };

  // Validate required fields on submit (add additional validations if needed)
  const validateFields = () => {
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Campaign name is required.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    if (showGoal && (!formData.goal || parseFloat(formData.goal) <= 0)) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid campaign goal.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    if (showDates && (!formData.startDate || !formData.endDate)) {
      toast({
        title: "Validation Error",
        description: "Please provide valid start and end dates.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    if (!urlAvailable) {
      toast({
        title: "URL Unavailable",
        description:
          "The provided URL is already in use. Please choose another.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) return;

    const goal = showGoal ? formatDollarsToCents(formData.goal) : 0;
    const startDate = showDates ? new Date(formData.startDate) : null;
    const endDate = showDates ? new Date(formData.endDate) : null;

    const campaignData = {
      name: formData.name,
      goal,
      url: cleanedUrl,
      description: formData.description,
      status: formData.status,
      organizationId,
    };

    if (showDates) {
      campaignData.startDate = startDate;
      campaignData.endDate = endDate;
    }

    try {
      const response = await secureFetch(`/api/campaigns/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        data: campaignData,
      });

      if (response.ok) {
        let bannerUpdateWarning = "";
        if (bannerImageDataUrl) {
          const bannerResponse = await secureFetch(
            `/api/campaigns/${id}/banner`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              data: { imageDataUrl: bannerImageDataUrl },
            },
          );
          if (!bannerResponse.ok) {
            bannerUpdateWarning =
              bannerResponse.message ||
              "Campaign updated, but banner upload failed.";
          }
        } else if (removeBannerImage && currentBannerUrl) {
          const removeResponse = await secureFetch(
            `/api/campaigns/${id}/banner`,
            {
              method: "DELETE",
            },
          );
          if (!removeResponse.ok) {
            bannerUpdateWarning =
              removeResponse.message ||
              "Campaign updated, but banner removal failed.";
          }
        }

        toast({
          title: "Campaign Updated",
          description:
            bannerUpdateWarning ||
            "Your campaign has been updated successfully!",
          status: bannerUpdateWarning ? "warning" : "success",
          duration: 5000,
          isClosable: true,
        });
        navigate("/campaigns");
      } else {
        throw new Error(response.message || "Failed to update campaign");
      }
    } catch (error) {
      toast({
        title: "Update Failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={5}>
      <VStack as="form" onSubmit={handleSubmit} spacing={4}>
        <FormControl isRequired>
          <FormLabel htmlFor="name">Campaign Name</FormLabel>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </FormControl>

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="goalToggle" mb="0">
            Set Target Amount
          </FormLabel>
          <Switch
            id="goalToggle"
            isChecked={showGoal}
            onChange={() => {
              setShowGoal((prev) => !prev);
              setFormData((prev) => ({ ...prev, goal: "" }));
            }}
          />
        </FormControl>
        {showGoal && (
          <FormControl isRequired>
            <FormLabel htmlFor="goal">Goal</FormLabel>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                color="gray.500"
                fontSize="1.4em"
              >
                <FaDollarSign />
              </InputLeftElement>
              <Input
                id="goal"
                name="goal"
                type="number"
                value={formData.goal}
                onChange={handleChange}
                required
              />
            </InputGroup>
          </FormControl>
        )}

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="datesToggle" mb="0">
            Set Start and End Dates
          </FormLabel>
          <Switch
            id="datesToggle"
            isChecked={showDates}
            onChange={() => setShowDates((prev) => !prev)}
          />
        </FormControl>
        {showDates && (
          <HStack spacing={4} width="full">
            <FormControl isRequired>
              <FormLabel htmlFor="startDate">Start Date</FormLabel>
              <Input
                id="startDate"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel htmlFor="endDate">End Date</FormLabel>
              <Input
                id="endDate"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleChange}
                required
              />
            </FormControl>
          </HStack>
        )}

        <FormControl isRequired>
          <FormLabel htmlFor="url">URL</FormLabel>
          <HStack>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                color="gray.500"
                fontSize="1.4em"
              >
                <FiLink />
              </InputLeftElement>
              <Input
                id="url"
                name="url"
                type="text"
                value={formData.url}
                onChange={handleChange}
                required
              />
            </InputGroup>
            <Button onClick={generateRandomUrl}>Generate</Button>
          </HStack>
          <Flex
            mt={1}
            direction={{ base: "column", sm: "row" }}
            align="flex-start"
            flexWrap="wrap"
          >
            <Text fontSize="sm" color="gray.500" wordBreak="break-all">
              Preview: https://purelygive.com/donate/{cleanedUrl}
            </Text>
            <Text
              ml={{ base: 0, sm: 2 }}
              mt={{ base: 1, sm: 0 }}
              fontSize="sm"
              color={urlAvailable ? "green.500" : "red.500"}
            >
              {urlAvailable ? "URL is available" : "URL is not available"}
            </Text>
          </Flex>
        </FormControl>

        <FormControl>
          <FormLabel htmlFor="description">Description</FormLabel>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl>
          <FormLabel htmlFor="bannerImage">Banner Image (Optional)</FormLabel>
          <Input
            id="bannerImage"
            name="bannerImage"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleBannerImageChange}
            p={1}
          />
          <Text fontSize="sm" color="gray.600" mt={1}>
            Recommended: {BANNER_IMAGE_RECOMMENDATION.recommendedWidth}x
            {BANNER_IMAGE_RECOMMENDATION.recommendedHeight}px. Minimum:{" "}
            {BANNER_IMAGE_RECOMMENDATION.minimumWidth}x
            {BANNER_IMAGE_RECOMMENDATION.minimumHeight}px. Max{" "}
            {BANNER_IMAGE_RECOMMENDATION.maxFileSizeMB}MB.
          </Text>
          {bannerImageFileName && (
            <Text fontSize="sm" color="gray.700" mt={1}>
              Selected: {bannerImageFileName}
            </Text>
          )}
          {bannerImageWarning && (
            <Text fontSize="sm" color="orange.500" mt={1}>
              {bannerImageWarning}
            </Text>
          )}
          {(bannerImagePreview || (currentBannerUrl && !removeBannerImage)) && (
            <Box mt={3} borderWidth="1px" borderRadius="md" overflow="hidden">
              <Image
                src={bannerImagePreview || currentBannerUrl}
                alt="Campaign banner preview"
                width="100%"
                maxH="160px"
                objectFit="cover"
              />
            </Box>
          )}
          {currentBannerUrl && !bannerImagePreview && (
            <Button
              mt={2}
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={() => setRemoveBannerImage((prev) => !prev)}
            >
              {removeBannerImage
                ? "Keep Existing Banner"
                : "Remove Existing Banner"}
            </Button>
          )}
          {removeBannerImage && (
            <Text mt={2} fontSize="sm" color="red.500">
              Existing banner will be removed when you save.
            </Text>
          )}
        </FormControl>

        <Button mt={4} colorScheme="orange" type="submit">
          Update Campaign
        </Button>
      </VStack>
    </Box>
  );
};

export default EditCampaign;
